import { type OperationId, operationIds } from "@gen/app-access-config";
import { CoreQuery } from "@gen/app-query";
import Button from "@mui/material/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { ActionDialog } from "~/components/action_dialog";

interface ReleaseFundsActionProps {
  canExecuteForStatus: (operationId: OperationId) => boolean;
  userTransactionRole: "buyer" | "seller";
  transactionId: number;
  isF2f?: boolean;
}

export function ReleaseFunds({
  canExecuteForStatus,
  userTransactionRole,
  transactionId,
  isF2f,
}: ReleaseFundsActionProps) {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const onMutationError = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const { mutate: releaseF2fFundsMutation, isPending: isReleasingF2fFunds } =
    CoreQuery.useAdmin_releaseF2fFundsMutationWithParameters({
      meta: {
        success: `Funds released to ${userTransactionRole}`,
      },
      onSuccess: () => {
        if (transactionId) {
          queryClient.invalidateQueries({
            queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
          });
        }
      },
      onError: onMutationError,
    });

  const {
    mutate: releaseOnlineFundsMutation,
    isPending: isReleasingOnlineFunds,
  } = CoreQuery.useAdmin_releaseOnlineFundsMutationWithParameters({
    meta: {
      success: `Funds released to ${userTransactionRole}`,
    },
    onSuccess: () => {
      if (transactionId) {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
        });
      }
      setDialogOpen(false);
    },
    onError: onMutationError,
  });

  const canExecute = isF2f
    ? canExecuteForStatus(operationIds["core_admin.releaseF2fFunds"])
    : canExecuteForStatus(operationIds["core_admin.releaseOnlineFunds"]);

  if (!canExecute) {
    return null;
  }

  const handleReleaseFunds = () => {
    if (isF2f) {
      releaseF2fFundsMutation({
        transaction_id: transactionId,
        to: userTransactionRole,
        releaseF2fFundsBody: {},
      });
    } else {
      releaseOnlineFundsMutation({
        transaction_id: transactionId,
        to: userTransactionRole,
        releaseOnlineFundsBody: {},
      });
    }
  };
  const isLoading = isReleasingOnlineFunds || isReleasingF2fFunds;

  let buttonLabel = "";
  if (userTransactionRole === "buyer") {
    buttonLabel = "Refund funds to ";
  } else if (userTransactionRole === "seller") {
    buttonLabel = "Release funds to ";
  }
  buttonLabel +=
    userTransactionRole[0].toUpperCase() + userTransactionRole.slice(1);

  return (
    <>
      <Button
        variant="contained"
        fullWidth
        onClick={() => setDialogOpen(true)}
        loading={isLoading}
        size="large"
        data-testid={`release-funds-${userTransactionRole}-button`}
      >
        {buttonLabel}
      </Button>
      {isDialogOpen && (
        <ActionDialog
          heading="Release funds to Seller"
          message="Are you sure you want to release funds to the Seller?"
          confirmButtonMessage="Yes, release funds to Seller"
          cancelButtonMessage="No, cancel"
          isConfirming={isLoading}
          confirm={handleReleaseFunds}
          cancel={() => setDialogOpen(false)}
          dataTestId={`release-funds-${userTransactionRole}`}
        />
      )}
    </>
  );
}
